'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import ProfileSection from './ProfileSection';
import BookingsOverview from './BookingsOverview';
import SavedTrips from './SavedTrips';
import PreferencesSection from './PreferencesSection';

type TabType = 'profile' | 'bookings' | 'saved-trips' | 'preferences';

const UserDashboardInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('profile');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const tabs = [
    { id: 'profile' as TabType, label: 'Profile', icon: 'UserCircleIcon' },
    { id: 'bookings' as TabType, label: 'My Bookings', icon: 'TicketIcon' },
    { id: 'saved-trips' as TabType, label: 'Saved Trips', icon: 'BookmarkIcon' },
    { id: 'preferences' as TabType, label: 'Preferences', icon: 'Cog6ToothIcon' },
  ];

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-surface via-white to-primary/5 pt-24 pb-16">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-gray-200 rounded-lg w-1/3"></div>
            <div className="h-64 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-surface via-white to-primary/5 pt-24 pb-16">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-4xl md:text-5xl font-black text-foreground mb-2">
            User Dashboard
          </h1>
          <p className="text-lg text-muted-foreground">
            Manage your travel profile and bookings
          </p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-brand mb-8 border border-border overflow-hidden">
          <div className="flex flex-wrap border-b border-border">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 lg:px-6 py-4 font-bold transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-surface'
                }`}
              >
                <Icon name={tab.icon as any} size={20} />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="p-4 lg:p-6">
            {activeTab === 'profile' && <ProfileSection />}
            {activeTab === 'bookings' && <BookingsOverview />}
            {activeTab === 'saved-trips' && <SavedTrips />}
            {activeTab === 'preferences' && <PreferencesSection />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDashboardInteractive;